/**
 * Resolve checkout lines for cart GTM events (legacy useCheckout parity).
 */

import type { AddLineInput } from '@/lib/checkout/hooks/useCheckoutCart';
import { extractPreorderData } from '@/lib/checkout/preOrder';
import { getPreOrderChargeAmount } from '@/lib/checkout/preOrderSummary';
import type { CheckoutData, CheckoutLineData } from '@/lib/checkout/types';
import { getCountryCodeFromChannel } from '@/lib/const';
import type { GtmCheckoutLineSource } from '@/lib/gtm/ecommerce';
import { isTaxIncluded } from '@/lib/tax';

/** Checkout fields used for cart GTM totals — accepts Valtio readonly snapshots. */
export type GtmCartCheckoutSource = {
  readonly lines?: ReadonlyArray<GtmCheckoutLineSource> | null;
  readonly voucherCode?: string | null;
  readonly totalPrice?: {
    readonly gross?: { readonly amount?: number };
    readonly net?: { readonly amount?: number };
  } | null;
  readonly channel?: { readonly slug?: string | null } | null;
  readonly orderType?: string | null;
  readonly preOrderData?: string | null;
};

/**
 * Find lines added in the latest checkout snapshot (strap + auto-added buckle).
 *
 * Example Input:
 *   checkout with new strap line, input.variantId = strap variant id
 *
 * Example Output:
 *   [strapLine, buckleLine]
 */
export function resolveAddedCheckoutLines(
  checkout: CheckoutData | null | undefined,
  input: AddLineInput
): CheckoutLineData[] {
  if (!checkout?.lines?.length) {
    return [];
  }

  const linesNewestFirst = [...checkout.lines].reverse();
  const added: CheckoutLineData[] = [];

  const mainLine = linesNewestFirst.find((line) => line.variant?.id === input.variantId);
  if (mainLine) {
    added.push(mainLine);
  }

  const buckleVariantId = input.strapRelatedProducts?.buckleId;
  if (buckleVariantId) {
    const buckleLine = linesNewestFirst.find((line) => line.variant?.id === buckleVariantId);
    if (buckleLine && !added.some((item) => item.id === buckleLine.id)) {
      added.push(buckleLine);
    }
  }

  return added;
}

/**
 * Collect a removed line and its child strap / buckle lines (watch removal only).
 *
 * Example Input:
 *   checkout lines, lineId of watch line with related strap
 *
 * Example Output:
 *   [watchLine, strapLine, buckleLine]
 */
export function resolveRemovedCheckoutLines(
  checkoutLines: readonly CheckoutLineData[] | undefined,
  lineId: string
): CheckoutLineData[] {
  if (!checkoutLines?.length) {
    return [];
  }

  const line = checkoutLines.find((item) => item.id === lineId);
  if (!line) {
    return [];
  }

  // Only follow child relationships (watch → strap/buckle). Do not walk up to the
  // parent watch when a strap or buckle is removed independently.
  const relatedStrap = line.relatedProductStrapId
    ? checkoutLines.find((item) => item.id === line.relatedProductStrapId)
    : undefined;
  const relatedBuckle = line.relatedProductBuckleId
    ? checkoutLines.find((item) => item.id === line.relatedProductBuckleId)
    : undefined;

  return [line, relatedStrap, relatedBuckle].filter((item): item is CheckoutLineData => !!item);
}

/**
 * VAT-aware cart total for GTM cart events.
 *
 * Example Input:
 *   checkout with totalPrice, channel slug "gb-gbp"
 *
 * Example Output:
 *   net or gross amount depending on channel VAT rules
 */
export function resolveCheckoutCartTotalValue(
  checkout: GtmCartCheckoutSource | null | undefined,
  channelSlug?: string | null
): number | undefined {
  if (!checkout?.totalPrice) {
    return undefined;
  }

  const countryCode = getCountryCodeFromChannel(channelSlug ?? checkout.channel?.slug ?? undefined);
  const includesVat = countryCode ? isTaxIncluded(countryCode) : true;
  return includesVat ? checkout.totalPrice.gross?.amount : checkout.totalPrice.net?.amount;
}

/**
 * Cart/checkout GTM `ecommerce.value` — deposit for pre-order, full total otherwise.
 *
 * Example Input:
 *   pre-order checkout with deposit 3000, channel "us"
 *
 * Example Output:
 *   3000
 */
export function resolveCheckoutCartGtmValue(
  checkout: GtmCartCheckoutSource | null | undefined,
  channelSlug?: string | null
): number | undefined {
  const fullTotal = resolveCheckoutCartTotalValue(checkout, channelSlug);
  const preOrderData = extractPreorderData(checkout);
  if (!preOrderData || fullTotal === undefined) {
    return fullTotal;
  }

  const countryCode = getCountryCodeFromChannel(
    channelSlug ?? checkout?.channel?.slug ?? undefined
  );
  const showTax = countryCode ? isTaxIncluded(countryCode) : true;
  return getPreOrderChargeAmount(preOrderData, showTax);
}
